import { useState, useEffect, useMemo } from "react";
import { Autocomplete, TextField, CircularProgress } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import {
  itemsApi, vendorsApi, customersApi, itemPickerApi, warehousesApi,
  type ItemDto, type VendorDto, type CustomerDto, type ItemPickerRow,
} from "@/api/endpoints";

interface PickerProps<T> {
  value: T | null;
  onChange: (v: T | null) => void;
  label: string;
  disabled?: boolean;
  required?: boolean;
  error?: boolean;
  helperText?: string;
}

function useDebouncedValue(value: string, delay = 300) {
  const [d, setD] = useState(value);
  useEffect(() => {
    const h = setTimeout(() => setD(value), delay);
    return () => clearTimeout(h);
  }, [value, delay]);
  return d;
}

export function ItemPicker({ value, onChange, label, disabled, required, error, helperText }: PickerProps<ItemDto>) {
  const [input, setInput] = useState("");
  const debounced = useDebouncedValue(input);
  const q = useQuery({
    queryKey: ["items-picker", debounced],
    queryFn: () => itemsApi.list({ search: debounced || undefined, page: 1, pageSize: 25 }),
    staleTime: 30_000,
  });
  return (
    <Autocomplete<ItemDto>
      value={value}
      options={q.data?.items ?? []}
      inputValue={input}
      onInputChange={(_, v) => setInput(v)}
      onChange={(_, v) => onChange(v)}
      disabled={disabled}
      loading={q.isLoading}
      getOptionLabel={(o) => `${o.sku} — ${o.name}${o.strength ? ` (${o.strength})` : ""}`}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      filterOptions={(x) => x}
      size="small" fullWidth
      renderInput={(params) => (
        <TextField {...params} label={`${label}${required ? " *" : ""}`}
          error={error} helperText={helperText}
          InputProps={{
            ...params.InputProps,
            endAdornment: <>
              {q.isLoading ? <CircularProgress size={16} /> : null}
              {params.InputProps.endAdornment}
            </>,
          }} />
      )}
    />
  );
}

/**
 * Item picker backed by the universal picker query, for screens that need the catalog row —
 * MRP, schedule category, pack details — rather than the plain item-master row `ItemPicker`
 * returns.
 *
 * <p>Searches server-side, like every other picker here. The pricing screens used to fetch one
 * fixed page and let MUI's default `filterOptions` filter it, which quietly limited them to the
 * alphabetical first hundred items: anything further down the catalog could not be found at all,
 * however it was spelled. `filterOptions={(x) => x}` is what turns that off — without it the
 * server search is re-filtered against the box's text and the fix does nothing.</p>
 *
 * <p>Stock policy is fixed at `Any`. Pricing an item is not dispensing it, and a rate has to be
 * settable for something that is out of stock — which is often exactly when it is being set up.
 * The warehouse is required by the endpoint but immaterial under that policy, so the first one
 * is resolved here rather than made every caller's problem.</p>
 */
export function CatalogItemPicker({
  value, onChange, label, placeholder, disabled, required, error, helperText,
}: {
  value: ItemPickerRow | null;
  onChange: (v: ItemPickerRow | null) => void;
  /** Omit for a bare in-table field; `placeholder` then carries the hint. */
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  error?: boolean;
  helperText?: string;
}) {
  const [input, setInput] = useState("");
  const debounced = useDebouncedValue(input);

  const warehouses = useQuery({
    queryKey: ["warehouses", "all"],
    queryFn: () => warehousesApi.list({ pageSize: 50 }),
    staleTime: 5 * 60_000,
  });
  const warehouseId = warehouses.data?.items[0]?.id ?? "";

  const q = useQuery({
    queryKey: ["item-picker", "catalog", warehouseId, debounced],
    queryFn: () => itemPickerApi.picker({
      stockPolicy: "Any", warehouseId, q: debounced || undefined, page: 1, pageSize: 50,
    }),
    enabled: !!warehouseId,
    staleTime: 30_000,
  });

  // A chosen item drops out of the options as soon as the search moves on. Keeping it in means
  // the field still renders its own selection instead of blanking, and MUI stops warning that
  // the value is not one of the options.
  const options = useMemo(() => {
    const rows = q.data ?? [];
    return value && !rows.some(r => r.id === value.id) ? [value, ...rows] : rows;
  }, [q.data, value]);

  return (
    <Autocomplete<ItemPickerRow>
      value={value}
      options={options}
      inputValue={input}
      onInputChange={(_, v) => setInput(v)}
      onChange={(_, v) => onChange(v)}
      disabled={disabled}
      loading={q.isLoading}
      getOptionLabel={(o) => `${o.sku} — ${o.name}${o.strength ? ` (${o.strength})` : ""}`}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      filterOptions={(x) => x}
      noOptionsText={debounced ? "No item matches" : "Type to search the catalog"}
      size="small" fullWidth
      renderInput={(params) => (
        <TextField {...params}
          label={label ? `${label}${required ? " *" : ""}` : undefined}
          placeholder={placeholder}
          error={error} helperText={helperText}
          InputProps={{
            ...params.InputProps,
            endAdornment: <>
              {q.isLoading ? <CircularProgress size={16} /> : null}
              {params.InputProps.endAdornment}
            </>,
          }} />
      )}
    />
  );
}

export function VendorPicker({ value, onChange, label, disabled, required, error, helperText }: PickerProps<VendorDto>) {
  const [input, setInput] = useState("");
  const debounced = useDebouncedValue(input);
  const q = useQuery({
    queryKey: ["vendors-picker", debounced],
    queryFn: () => vendorsApi.list({ search: debounced || undefined, isActive: true, page: 1, pageSize: 25 }),
    staleTime: 60_000,
  });
  return (
    <Autocomplete<VendorDto>
      value={value}
      options={q.data?.items ?? []}
      inputValue={input}
      onInputChange={(_, v) => setInput(v)}
      onChange={(_, v) => onChange(v)}
      disabled={disabled}
      loading={q.isLoading}
      getOptionLabel={(o) => `${o.code} — ${o.name}`}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      filterOptions={(x) => x}
      size="small" fullWidth
      renderInput={(params) => (
        <TextField {...params} label={`${label}${required ? " *" : ""}`}
          error={error} helperText={helperText}
          InputProps={{
            ...params.InputProps,
            endAdornment: <>
              {q.isLoading ? <CircularProgress size={16} /> : null}
              {params.InputProps.endAdornment}
            </>,
          }} />
      )}
    />
  );
}

export function CustomerPicker({ value, onChange, label, disabled, required, error, helperText }: PickerProps<CustomerDto>) {
  const [input, setInput] = useState("");
  const debounced = useDebouncedValue(input);
  const q = useQuery({
    queryKey: ["customers-picker", debounced],
    queryFn: () => customersApi.list({ search: debounced || undefined, isActive: true, page: 1, pageSize: 25 }),
    staleTime: 30_000,
  });
  return (
    <Autocomplete<CustomerDto>
      value={value}
      options={q.data?.items ?? []}
      inputValue={input}
      onInputChange={(_, v) => setInput(v)}
      onChange={(_, v) => onChange(v)}
      disabled={disabled}
      loading={q.isLoading}
      getOptionLabel={(o) => `${o.code} — ${o.name}${o.uhid ? ` [${o.uhid}]` : ""}`}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      filterOptions={(x) => x}
      size="small" fullWidth
      renderInput={(params) => (
        <TextField {...params} label={`${label}${required ? " *" : ""}`}
          error={error} helperText={helperText}
          InputProps={{
            ...params.InputProps,
            endAdornment: <>
              {q.isLoading ? <CircularProgress size={16} /> : null}
              {params.InputProps.endAdornment}
            </>,
          }} />
      )}
    />
  );
}
